'use client';

import { motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { ReactNode, useEffect, useRef, useState } from 'react';
import FadeEffect from './FadeEffect';

interface PageTransitionProps {
  children: ReactNode;
}

// Longer than the 0.5s enter transition. If framer-motion's enter animation has
// not committed opacity:1 by now (e.g. the route mounted while the tab was
// backgrounded and requestAnimationFrame was paused, or the animation was
// interrupted by a fast re-navigation), we force the end-state imperatively so
// content can never stay trapped at opacity:0 — a blank white screen.
const FAILSAFE_MS = 800;

const PageTransition: React.FC<PageTransitionProps> = ({ children }) => {
  const pathname = usePathname();
  const ref = useRef<HTMLDivElement>(null);

  // framer-motion resolves `initial` into an inline style when rendering on the server,
  // so an enter animation starting at opacity 0 would serve every page body as
  // `style="opacity:0"` — hidden to anything that applies CSS without running JS. Skip
  // the enter animation on the first paint (`initial={false}` starts at the `animate`
  // state) and enable it once mounted, so client-side navigations still fade. Because
  // framer-motion only reads `initial` when an element mounts, flipping this afterwards
  // leaves the element already on screen untouched.
  const [enableEnterAnimation, setEnableEnterAnimation] = useState(false);

  useEffect(() => {
    setEnableEnterAnimation(true);
  }, []);

  useEffect(() => {
    const forceVisible = () => {
      if (ref.current) {
        ref.current.style.opacity = '1';
      }
    };

    // Belt-and-suspenders: a timeout failsafe and a visibility listener. rAF is
    // paused while the tab is hidden, so the enter animation can stall at 0;
    // both paths guarantee the content ends up visible.
    const timer = setTimeout(forceVisible, FAILSAFE_MS);
    const onVisible = () => {
      if (!document.hidden) {
        forceVisible();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [pathname]);

  return (
    <>
      <FadeEffect />
      <motion.div
        ref={ref}
        key={pathname}
        initial={enableEnterAnimation ? { opacity: 0 } : false}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        // If the animation completes normally, mirror the visible end-state into
        // the inline style so it agrees with the failsafe path above.
        onAnimationComplete={() => {
          if (ref.current) {
            ref.current.style.opacity = '1';
          }
        }}
      >
        {children}
      </motion.div>
    </>
  );
};

export default PageTransition;
